import { useEffect, useState } from "react";
import { api } from "../../../lib/api";

export type ImportCapabilities = {
  supported_entity_types: string[];
  accepted_file_extensions: string[]; // dot-prefixed, lowercase
  accepted_mime_types: string[];
  max_file_bytes: number;
  max_rows: number;
  max_columns: number;
  max_cell_length: number;
  sample_row_limit: number;
};

/**
 * Conservative fallback for the client-side sanity checks when the
 * capabilities endpoint is unreachable. The backend upload endpoint
 * remains authoritative and will reject anything above its true limits.
 *
 * These values are deliberately smaller (or equal) than the current
 * backend defaults so no user ever sees "your file fits" here and then
 * gets rejected by the server on upload. If ops raise the server limits
 * later, users still see this conservative floor until the network
 * recovers — which is the intended safe fallback.
 */
export const FALLBACK_CAPABILITIES: ImportCapabilities = {
  supported_entity_types: ["contact"],
  accepted_file_extensions: [".csv"],
  accepted_mime_types: ["text/csv"],
  max_file_bytes: 1 * 1024 * 1024, // 1 MiB (conservative)
  max_rows: 1000,
  max_columns: 50,
  max_cell_length: 4096,
  sample_row_limit: 5,
};

let _cache: ImportCapabilities | null = null;
let _inflight: Promise<ImportCapabilities> | null = null;

async function fetchOnce(): Promise<ImportCapabilities> {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = api
    .get<ImportCapabilities>("/import/capabilities")
    .then((r) => {
      _cache = normalize(r.data);
      _inflight = null;
      return _cache;
    })
    .catch((e) => {
      _inflight = null;
      throw e;
    });
  return _inflight;
}

function normalize(raw: unknown): ImportCapabilities {
  const r = (raw ?? {}) as Partial<ImportCapabilities>;
  return {
    supported_entity_types: Array.isArray(r.supported_entity_types)
      ? r.supported_entity_types
      : FALLBACK_CAPABILITIES.supported_entity_types,
    accepted_file_extensions: Array.isArray(r.accepted_file_extensions)
      ? r.accepted_file_extensions.map((e) => String(e).toLowerCase())
      : FALLBACK_CAPABILITIES.accepted_file_extensions,
    accepted_mime_types: Array.isArray(r.accepted_mime_types)
      ? r.accepted_mime_types
      : FALLBACK_CAPABILITIES.accepted_mime_types,
    max_file_bytes: Number.isFinite(r.max_file_bytes as number)
      ? Number(r.max_file_bytes)
      : FALLBACK_CAPABILITIES.max_file_bytes,
    max_rows: Number.isFinite(r.max_rows as number)
      ? Number(r.max_rows)
      : FALLBACK_CAPABILITIES.max_rows,
    max_columns: Number.isFinite(r.max_columns as number)
      ? Number(r.max_columns)
      : FALLBACK_CAPABILITIES.max_columns,
    max_cell_length: Number.isFinite(r.max_cell_length as number)
      ? Number(r.max_cell_length)
      : FALLBACK_CAPABILITIES.max_cell_length,
    sample_row_limit: Number.isFinite(r.sample_row_limit as number)
      ? Number(r.sample_row_limit)
      : FALLBACK_CAPABILITIES.sample_row_limit,
  };
}

/** Test-only helper — resets the module cache between Vitest suites. */
export function __resetCapabilitiesCache(): void {
  _cache = null;
  _inflight = null;
}

/**
 * Loads the effective import capabilities from the backend. On failure
 * returns the conservative fallback with `error` set so consumers can
 * surface a small "using safe defaults; the server will make the final
 * decision" hint. The server upload endpoint always has the final say
 * regardless of what this hook returns.
 */
export function useImportCapabilities(): {
  caps: ImportCapabilities;
  loading: boolean;
  error: unknown;
} {
  const [caps, setCaps] = useState<ImportCapabilities | null>(_cache);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState<boolean>(!_cache);

  useEffect(() => {
    let cancelled = false;
    if (_cache) {
      setCaps(_cache);
      setLoading(false);
      return;
    }
    fetchOnce()
      .then((c) => {
        if (!cancelled) {
          setCaps(c);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return {
    caps: caps ?? FALLBACK_CAPABILITIES,
    loading,
    error,
  };
}

// ---- Presentational helpers (kept here so consumers don't recompute) ----

export function formatBytes(n: number): string {
  if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(n % (1024 * 1024) === 0 ? 0 : 2)} MiB`;
  if (n >= 1024) return `${(n / 1024).toFixed(0)} KiB`;
  return `${n} B`;
}

export function formatCount(n: number): string {
  return n.toLocaleString();
}
